import { useCallback, useState } from 'react';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';

export type DialogState = 'open' | 'closed';

export const useDialogControl = (
  state: DialogState = 'closed',
  { logger }: LoggingContext = {},
) => {
  const [dialogState, setDialogState] = useState<DialogState>(state);

  const openCallback = useCallback(() => setDialogState('open'), []);
  const closeCallback = useCallback(() => setDialogState('closed'), []);

  logger?.debug({ dialogState });

  return {
    state: dialogState,

    isOpen: dialogState === 'open',
    isClosed: dialogState === 'closed',

    open: openCallback,
    close: closeCallback,
  };
};

export const useDialog = function <TData = unknown>(
  state: DialogState = 'closed',
  initialData: TData | undefined = undefined,
  { logger }: LoggingContext = {},
) {
  const dialogControl = useDialogControl(state);
  const [dialogData, setDialogData] = useState<TData | undefined>(initialData);

  const open = useCallback(
    (data?: TData) => {
      logger?.debug({ data }, 'Opening dialog');

      if (!isNil(data)) {
        logger?.debug({ data }, 'Setting dialog data');
        setDialogData(data);
      }

      dialogControl.open();
    },
    [dialogControl.open, logger?.debug],
  );

  const close = useCallback(
    /**
     * Closes the dialog and optionally resets its stored data.
     *
     * @param reset - If true, reset the dialog data when closing. Defaults to true.
     * @default true
     */
    (reset: boolean = true) => {
      logger?.debug({ reset }, 'Closing dialog');

      if (reset === true) {
        logger?.debug('Resetting dialog data');
        setDialogData(undefined);
      }

      dialogControl.close();
    },
    [dialogControl.close, logger?.debug],
  );

  logger?.debug({ dialogState: dialogControl.state, dialogData });

  return {
    ...dialogControl,

    open,
    close,

    data: dialogData,
  };
};
